/**
 * @file        src/pages/flight/AmendmentActionModal.jsx
 * @description Preview→confirm flow for the supplier amendment actions that return
 *              money — Void and Full Refund. Mirrors CancelBookingModal: it fetches
 *              the refund quote from the airline (via the booking backend / TripJack)
 *              and only commits after the admin confirms. Void and Full Refund are
 *              async at TripJack, so a submitted action reads "…Requested" until the
 *              airline confirms — never a fake completion.
 * @author      Kinetika Labs
 * @created     2026-07-19
 * @changelog
 *   - 2026-07-19 Claude — Initial (Phase 2: Void + Full Refund actions).
 *   - 2026-09-15 Claude — API errors read via apiErrorMessage(): the error handler returns {error:{code,message}}, and putting that object into state/toast threw "Objects are not valid as a React child", blanking the whole app.
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Icon from '../../components/Icon';
import { amendmentPreview, amendmentSubmit } from '../../api/flightBookings';
import { apiErrorMessage } from '../../api/client';

const inr2 = (n) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// type → human label + the confirm verb.
const META = {
  VOID: { label: 'Void', verb: 'Void booking', gerund: 'Voiding' },
  FULL_REFUND: { label: 'Full Refund', verb: 'Request full refund', gerund: 'Requesting refund' },
};

export default function AmendmentActionModal({ booking, type, onClose }) {
  const qc = useQueryClient();
  const meta = META[type] || { label: type, verb: 'Confirm', gerund: 'Submitting' };
  const [phase, setPhase] = useState('loading'); // loading | preview | blocked | submitting | done | error
  const [info, setInfo] = useState(null);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    amendmentPreview(booking.id, type)
      .then((res) => {
        if (!alive) return;
        if (res && res.actionable === false) { setInfo(res); setPhase('blocked'); }
        else { setInfo(res); setPhase('preview'); }
      })
      .catch((e) => { if (alive) { setErr(apiErrorMessage(e, 'Could not load amendment charges.')); setPhase('error'); } });
    return () => { alive = false; };
  }, [booking.id, type]);

  const submit = () => {
    setPhase('submitting'); setErr('');
    amendmentSubmit(booking.id, type, reason || `${type} by admin`)
      .then((res) => {
        setInfo(res); setPhase('done');
        qc.invalidateQueries({ queryKey: ['flight-bookings'] });
        qc.invalidateQueries({ queryKey: ['flight-booking-details', booking.id] });
        qc.invalidateQueries({ queryKey: ['flight-booking-amendments', booking.id] });
      })
      .catch((e) => { setErr(apiErrorMessage(e, 'The amendment could not be submitted.')); setPhase('error'); });
  };

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 480 }}>
        <div className="lk-modal-head">
          <h3>{meta.label} · #{booking.id}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>

        <div className="det-body">
          {phase === 'loading' && <div className="empty">Fetching the refund from the airline…</div>}

          {phase === 'blocked' && (
            <>
              <p>{info?.message || 'This amendment cannot be processed online.'}</p>
              <div className="form-actions"><button className="btn ghost" onClick={onClose}>Close</button></div>
            </>
          )}

          {phase === 'preview' && (
            <>
              <p className="muted">{booking.booking_reference} · confirm the refund before submitting the {meta.label.toLowerCase()}.</p>
              <div className="det-fare">
                <div className="det-fare-row"><span>Refund amount</span><span><b>{inr2(info?.refundAmount)}</b></span></div>
                {info?.penalty != null ? <div className="det-fare-row"><span>Airline penalty</span><span>{inr2(info.penalty)}</span></div> : null}
                {info?.refundable === false ? <div className="field-error">Non-refundable — no refund will be issued.</div> : null}
              </div>
              <div className="field full">
                <label>Reason</label>
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={`${type} by admin`} />
              </div>
              <p className="muted" style={{ fontSize: '.85em' }}>Refund is processed by our team within 5–7 business days.</p>
              {err ? <div className="field-error">{err}</div> : null}
              <div className="form-actions">
                <button className="btn ghost" onClick={onClose}>Keep booking</button>
                <button className="btn danger" onClick={submit}>{meta.verb}</button>
              </div>
            </>
          )}

          {phase === 'submitting' && <div className="empty">{meta.gerund}…</div>}

          {phase === 'done' && (
            <>
              <p><b>{meta.label} submitted.</b></p>
              <div className="det-fare">
                <div className="det-fare-row"><span>Status</span><span>{info?.status || 'Requested'}</span></div>
                {info?.refundAmount != null ? <div className="det-fare-row"><span>Refund</span><span>{inr2(info.refundAmount)}</span></div> : null}
              </div>
              {info?.message ? <p className="muted" style={{ fontSize: '.85em' }}>{info.message}</p> : null}
              <div className="form-actions"><button className="btn primary" onClick={onClose}>Done</button></div>
            </>
          )}

          {phase === 'error' && (
            <>
              <div className="field-error">{err}</div>
              <div className="form-actions"><button className="btn ghost" onClick={onClose}>Close</button></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
